import { useState, useEffect } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Edit, Trash2, Save, X, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminPricingPage = () => {
  const { getAuthHeader } = useAdmin();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState({
    name: '', tagline: '', price: '', description: '', timeline: '',
    features: '', notIncluded: '', popular: false, order: 0
  });

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/pricing`);
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to load pricing');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      name: item.name, tagline: item.tagline, price: item.price, description: item.description,
      timeline: item.timeline, features: item.features.join('\n'),
      notIncluded: item.notIncluded.join('\n'), popular: item.popular, order: item.order
    });
  };

  const handleAdd = () => {
    setIsAdding(true);
    setFormData({ name: '', tagline: '', price: '', description: '', timeline: '',
      features: '', notIncluded: '', popular: false, order: items.length });
  };

  const handleCancel = () => {
    setEditingId(null); setIsAdding(false);
    setFormData({ name: '', tagline: '', price: '', description: '', timeline: '',
      features: '', notIncluded: '', popular: false, order: 0 });
  };

  const handleSave = async () => {
    try {
      const data = {
        name: formData.name, tagline: formData.tagline, price: formData.price,
        description: formData.description, timeline: formData.timeline,
        features: formData.features.split('\n').filter(Boolean),
        notIncluded: formData.notIncluded.split('\n').filter(Boolean),
        popular: formData.popular, order: formData.order
      };

      if (isAdding) {
        await axios.post(`${API}/pricing`, data, { headers: getAuthHeader() });
        toast.success('Package added');
      } else {
        await axios.put(`${API}/pricing/${editingId}`, data, { headers: getAuthHeader() });
        toast.success('Package updated');
      }
      fetchItems(); handleCancel();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this package?')) return;
    try {
      await axios.delete(`${API}/pricing/${id}`, { headers: getAuthHeader() });
      toast.success('Deleted');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard"><Button variant="outline" size="icon" className="rounded-full"><ArrowLeft size={18} /></Button></Link>
              <div><h1 className="font-heading text-3xl font-bold">Manage Pricing</h1></div>
            </div>
            {!isAdding && !editingId && <Button onClick={handleAdd} className="bg-[#10B981] rounded-full"><Plus size={18} className="mr-2" />Add Package</Button>}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {(isAdding || editingId) && (
          <div className="bg-white rounded-2xl p-8 border mb-8">
            <h2 className="font-heading text-2xl font-bold mb-6">{isAdding ? 'Add' : 'Edit'} Package</h2>
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div><Label>Name</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} /></div>
                <div><Label>Tagline</Label><Input value={formData.tagline} onChange={(e) => setFormData({...formData, tagline: e.target.value})} /></div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div><Label>Price</Label><Input value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} placeholder="Starting at $1,500" /></div>
                <div><Label>Timeline</Label><Input value={formData.timeline} onChange={(e) => setFormData({...formData, timeline: e.target.value})} placeholder="3-7 days" /></div>
              </div>
              <div><Label>Description</Label><Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={2} /></div>
              <div><Label>Features (one per line)</Label><Textarea value={formData.features} onChange={(e) => setFormData({...formData, features: e.target.value})} rows={6} /></div>
              <div><Label>Not Included (one per line)</Label><Textarea value={formData.notIncluded} onChange={(e) => setFormData({...formData, notIncluded: e.target.value})} rows={3} /></div>
              <div className="flex items-center space-x-2">
                <Checkbox checked={formData.popular} onCheckedChange={(checked) => setFormData({...formData, popular: checked})} />
                <Label>Mark as Popular</Label>
              </div>
              <div className="flex gap-4">
                <Button onClick={handleSave} className="bg-[#10B981] rounded-full"><Save size={18} className="mr-2" />Save</Button>
                <Button onClick={handleCancel} variant="outline" className="rounded-full"><X size={18} className="mr-2" />Cancel</Button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {items.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center border">
              <p className="text-slate-600 mb-4">No packages yet</p>
              <Button onClick={handleAdd} className="bg-[#10B981] rounded-full"><Plus size={18} className="mr-2" />Add Package</Button>
            </div>
          ) : (
            items.map((item) => (
              <div key={item.id} className="bg-white rounded-2xl p-6 border hover:border-[#10B981]">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-heading text-xl font-bold">{item.name}</h3>
                      {item.popular && <span className="bg-[#10B981] text-white px-2 py-1 rounded-full text-xs">Popular</span>}
                    </div>
                    <p className="text-sm text-slate-600 mb-2">{item.tagline}</p>
                    <p className="text-lg font-bold text-[#10B981] mb-2">{item.price}</p>
                    <p className="text-slate-600 mb-2">{item.description}</p>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="bg-slate-100 px-3 py-1 rounded-full">{item.timeline}</span>
                      <span className="bg-slate-100 px-3 py-1 rounded-full">{item.features.length} features</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleEdit(item)} variant="outline" size="icon" className="rounded-full"><Edit size={18} /></Button>
                    <Button onClick={() => handleDelete(item.id)} variant="outline" size="icon" className="rounded-full text-red-600"><Trash2 size={18} /></Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
